/**
 * Slice-owned typed API seam.
 *
 * The web-shell slice owns `src/lib/api/generated`. Until that branch lands, these
 * request/response shapes are copied from `packages/contracts/openapi.json`. The
 * integration pass can replace the function bodies with generated SDK calls while
 * preserving the feature query keys and component types below. Newer shapes are the generated types themselves.
 */

import type { ReportCardData, ReportType } from '@patchbay/ui'
import type {
  ActionClass,
  HubAgentRole,
  HubCallRow,
  HubIdentityCreate,
  HubIdentityKeyOut,
  HubIdentityOut,
  HubIdentityPatch,
  HubLivenessOut,
  LivenessOut,
  ProjectImportReviewOut,
  ProjectImportRow,
  RileyLabelIn,
  RileyOpOut,
  RileyOrgOut,
  SettingOut,
} from '../../lib/api/generated'
import type { ApiLane, QueueLaneSort } from '../queue/lanes'

export type {
  ActionClass,
  HubAgentRole,
  HubCallRow,
  HubIdentityCreate,
  HubIdentityKeyOut,
  HubIdentityOut,
  HubIdentityPatch,
  HubLivenessOut,
  LivenessOut,
  ProjectImportReviewOut,
  ProjectImportRow,
  RileyLabelIn,
  RileyOpOut,
  RileyOrgOut,
  SettingOut,
}

export type Role = 'manager' | 'cover' | 'tech' | 'billing'
export type DeskTokenState = 'connected' | 'refresh_failing' | 'not_connected'

export interface MeOut {
  id: string
  email: string
  name: string
  role: Role
  ui_mode: 'full' | 'simple'
  desk_agent_id: string | null
  desk_token: DeskTokenState
  /** Absent on older payloads. The profile treats a missing field as not connected. */
  calendar_token?: DeskTokenState
  /** Read-only Mail grant obtained through Zoho sign-in. */
  mail_token?: DeskTokenState
  can_write_desk: boolean
  is_superadmin?: boolean
  /** The saved signature (sanitised); null = none saved yet. */
  signature_html?: string | null
  csrf_token: string
}

export interface UserOut {
  id: string
  email: string
  name: string
  role: Role
  ui_mode: 'full' | 'simple'
  active: boolean
  is_superadmin?: boolean
  desk_agent_id: string | null
  desk_token: DeskTokenState
  mail_token?: DeskTokenState
  bound: boolean
  last_sign_in_at: string | null
  /** Utilisation denominator (round 13), a decimal string; 0 = not on the desk. Absent from older API builds. */
  weekly_capacity_hours?: string
  /** Billing-role delivery of Ready-to-bill item email, morning summary, and bell. */
  billing_ready_notifications_enabled?: boolean
  /** Other mailboxes that identify this person as staff (#926). */
  alternate_emails?: string[]
  /** #509 SmartPBX extension (Ewee Internal) that click-to-call rings first. */
  pbx_extension?: string | null
}

export interface InviteIn {
  email: string
  name: string
  role: Role
  desk_agent_id: string | null
  billing_ready_notifications_enabled?: boolean
}

export interface UserPatch {
  role?: Role
  active?: boolean
  desk_agent_id?: string | null
  name?: string
  /** 0-80, step 0.5. */
  weekly_capacity_hours?: number
  billing_ready_notifications_enabled?: boolean
  alternate_emails?: string[]
  /** #509: digits, or '' to clear. */
  pbx_extension?: string
}

export interface RemovedCaseOut {
  id: string
  number: string
  title: string
  customer_display?: string | null
  removed_at: string
  removed_by_user_id?: string | null
  reason: 'spam' | 'test' | 'duplicate' | 'created_by_mistake'
  duplicate_of_case_id?: string | null
  desk_delete_requested: boolean
}

export interface AuditChange {
  field: string
  before: unknown
  after: unknown
}

/** Names and sentences come from the API. Raw ids stay for the expanded row (#1069). */
export interface AuditRow {
  id: number
  actor_user_id: string | null
  actor_name: string
  action: string
  action_group: string
  action_sentence: string
  target_type: string | null
  target_id: string | null
  target_label: string | null
  target_href: string | null
  before: Record<string, unknown> | null
  after: Record<string, unknown> | null
  changes: AuditChange[]
  at: string
}

export interface AuditListQuery {
  cursor?: string
  actor?: string
  actionGroup?: string
  caseRef?: string
}

export interface FeatureFlagOut {
  id: string
  service: 'patchbay' | 'riley'
  label: string
  description: string
  value: boolean | number | string | null
  default_value: boolean | number | string | null
  source: 'stored' | 'environment' | 'external_env' | 'code_default' | 'invalid'
  effective_known: boolean
  value_kind: 'boolean' | 'choice' | 'number' | 'string'
  choices: string[]
  risk: 'standard' | 'customer_facing' | 'money' | 'autonomy'
  rollback_note: string
  setting_key: string
  env_name: string | null
  updated_at: string | null
  updated_by_user_id: string | null
  updated_by_name: string | null
}

export interface Page<T> {
  items: T[]
  next_cursor: string | null
  total: number | null
}

export type HealthSource =
  | 'riley_events'
  | 'riley_chat'
  | 'conduit'
  | 'desk_user'
  | 'eweetools'
  | 'sse'
  | 'jobs'
  | 'postmark_inbound'
  | 'procurement_catalog'

export interface IntegrationHealthOut {
  source: HealthSource
  last_ok_at: string | null
  last_error: string | null
  last_error_at: string | null
  lag_seconds: number | null
}

/** Whether an env-configured integration is set up (names only, never a value; #294). */
export interface IntegrationConfigOut {
  name: string
  label: string
  configured: boolean
  detail: string
}

export interface ZohoQuotaOut {
  org_day: string
  by_holder: Record<string, number>
  limit: number | null
  pct: number | null
  /** The org-day alert threshold (desk.daily_limit alert_pct of the total). */
  alert_at?: number | null
  alert?: boolean
  /** Holders whose count could not be read: the total is a floor, not the whole. */
  unreachable?: string[]
  complete?: boolean
}

export interface ConnectorBudgetOut {
  connector: string
  org_day: string
  calls: number | null
  soft_limit: number | null
  pct: number | null
  alert: boolean
  cache_hits: number
  per_minute: number | null
  note: string | null
}

export interface CostRateOut {
  id: string
  user_id: string
  loaded_hourly: string | number
  currency: string
  effective_from: string
  effective_to: string | null
  note: string | null
  set_by: string | null
}

export interface RileyRateOut {
  hourly: string | number
  currency: string
}

export interface CostRateIn {
  user_id: string
  loaded_hourly: number
  currency: string
  effective_from: string
  note: string | null
}

export interface ContractorRateOut {
  conduit_rate_id: string
  contractor_id: string
  rate_kind: string
  amount: string | number
  currency: string
  effective_from: string | null
  effective_to: string | null
  notes: string | null
}

export interface SearchHit {
  kind: 'case' | 'ticket' | 'customer' | 'contractor' | 'person' | 'knowledge' | 'account' | 'contact'
  id: string
  label: string
  sublabel: string | null
  href: string
}

export interface RileyTriageOut {
  ok: boolean
  ticket_id: string
  triage: Record<string, unknown>[]
  review: Record<string, unknown>[]
  deep_context: Record<string, unknown>[]
}

export type AutonomyMode = 'off' | 'suggest' | 'approve' | 'shadow_auto' | 'auto'

export interface AutomationMetrics {
  volume: number
  accepted: number
  dismissed: number
  expired: number
  accept_rate_pct: number | null
  edit_distance_pct: number | null
  edit_samples: number
  reversals: number
  reversal_rate_pct: number | null
  customer_reopens: number
  customer_reopen_rate_pct: number | null
  agreements: number
  agreement_items: number
  agreement_rate_pct: number | null
  disclosure_guard_blocks: number
  satisfaction_responses: number
  satisfaction_score: number | null
  satisfaction_good_pct: number | null
}

export interface AutomationTrend {
  volume: number
  accept_rate_points: number | null
  edit_distance_points: number | null
  reversal_rate_points: number | null
  customer_reopen_rate_points: number | null
  agreement_rate_points: number | null
  disclosure_guard_blocks: number
  satisfaction_responses: number
  satisfaction_score_points: number | null
  satisfaction_good_points: number | null
}

export interface AutomationPeriod {
  period: string
  days: number | null
  current: AutomationMetrics
  trend: AutomationTrend | null
}

export interface AutomationClassScore {
  action_class: ActionClass
  mode: AutonomyMode
  periods: AutomationPeriod[]
  promotion: {
    eligible: boolean
    ready_to_promote: boolean
    sample_size: number
    agreement_rate_pct: number | null
    disclosure_guard_blocks: number
    reasons: string[]
  }
}

export interface LicenceShadowRecent {
  product_name: string
  quantity_delta: number
  outcome: 'match' | 'mismatch' | 'no_human_action' | 'pending'
}

export interface LicenceShadowScore {
  current_streak: number
  required_streak: number
  matches: number
  mismatches: number
  eligible_for_suggest: boolean
  recent: LicenceShadowRecent[]
}

export interface AutomationScoreboardOut {
  computed_at: string
  thresholds: { min_items: number; min_agreement_pct: number }
  classes: AutomationClassScore[]
  licence_shadow: LicenceShadowScore
}

/** Riley GET /context through Patchbay (riley.md §4.4): the dossier lives here, not in the triage journal. */
export interface CaseContextOut {
  ok: boolean
  ticket_id: string
  state?: 'ok' | 'no_context' | 'unreachable'
  context?: { dossier?: { org_note?: string | null; handling?: string | null } | null } | null
}

export interface NotificationPrefsOut {
  digest_enabled: boolean
  digest_hour: number
  desk_mention_enabled: boolean
  push_enabled: boolean
  /** Email me when a teammate @mentions me (#113); absent from an older API = on. */
  mention_email_enabled?: boolean
  /** Case timeline order (#207); absent from an older API = newest first, kept in the browser. */
  timeline_order?: 'newest' | 'oldest'
  /** How many of the newest turns open expanded (#220); absent from an older API = 2. */
  expanded_turns?: number
  /** Queue order saved independently for each lane (#322); omitted lanes use who_owes. */
  queue_lane_orders?: Partial<Record<ApiLane, QueueLaneSort>>
  report_schedules?: ReportSchedule[]
  /** Staff case email per event (#667). Absent from an older API means immediate. */
  case_email?: Partial<Record<'case_updated' | 'needs_us' | 'awaiting', 'immediate' | 'digest' | 'off'>>
}

export interface ReportSchedule {
  id: string
  report_type: ReportType
  cadence: 'daily' | 'weekly'
  /** Weekly delivery day, Monday=0. */
  weekday: number
  customer_ref?: string | null
}

export interface ReportRunOut {
  id: string
  report_type: ReportType
  cadence: 'daily' | 'weekly'
  period_start: string
  period_end: string
  artifact: ReportCardData
  emailed_at?: string | null
  created_at: string
}

export type NotificationPrefsIn = Partial<NotificationPrefsOut>

export interface KnowledgeEntryOut {
  path: string
  title: string
  body: string
  tags: string[]
  sha: string
}

export interface KnowledgeProposalIn {
  entry_path: string
  proposed_body: string
  from_card_id?: string | null
}

export type KnowledgeProposalState = 'proposed' | 'approved' | 'rejected' | 'merged'

export interface KnowledgeProposalOut {
  id: string
  entry_path: string
  proposed_body: string
  state: KnowledgeProposalState
  pr_url: string | null
  created_at: string
}

export interface KnowledgeDecisionIn {
  action: 'approve' | 'reject'
  reason?: string | null
}

export type SnippetAudience = 'customer' | 'contractor' | 'internal'

export interface SnippetOut {
  id: string
  key: string
  title: string
  body_html: string
  body_text: string
  audience: SnippetAudience
  tags: string[]
  version: number
  updated_by: string | null
  updated_at: string
  active: boolean
}

export interface SnippetVersionOut {
  id: string
  snippet_id: string
  version: number
  key: string
  title: string
  body_html: string
  body_text: string
  audience: SnippetAudience
  tags: string[]
  active: boolean
  updated_by: string | null
  updated_at: string
  source: string
}

export interface SnippetUpsertIn {
  key: string
  title: string
  body_html: string
  audience: SnippetAudience
  tags: string[]
  active: boolean
}

export interface SnippetPatchIn {
  title: string
  body_html: string
  audience: SnippetAudience
  tags: string[]
  active: boolean
}

export interface DeskSnippetCandidate {
  external_id: string
  key: string
  title: string
  body_html: string
  body_text: string
  audience: SnippetAudience
  tags: string[]
  action: 'create' | 'update'
  note: string | null
}

export interface DeskSnippetDryRunOut {
  source_endpoint: string
  candidates: DeskSnippetCandidate[]
  create_count: number
  update_count: number
}

export interface DeskSnippetApplyOut {
  created: number
  updated: number
  skipped: number
}

export class ApiError extends Error {
  readonly status: number
  readonly code: string | null

  constructor(status: number, message: string, code: string | null = null) {
    super(message)
    this.name = 'ApiError'
    this.status = status
    this.code = code
  }
}

interface ErrorBody {
  detail?: unknown
  code?: unknown
}

async function request<T>(path: string, init: RequestInit = {}, csrfToken?: string): Promise<T> {
  const headers = new Headers(init.headers)
  headers.set('Accept', 'application/json')
  if (init.body !== undefined) headers.set('Content-Type', 'application/json')
  if (csrfToken) headers.set('X-CSRF-Token', csrfToken)

  const response = await fetch(`/api/v1${path}`, {
    ...init,
    credentials: 'include',
    headers,
  })
  if (!response.ok) {
    let body: ErrorBody = {}
    try {
      body = (await response.json()) as ErrorBody
    } catch {
      // An upstream/proxy can return HTML. Do not surface it in the UI.
    }
    const detail = typeof body.detail === 'string' ? body.detail : `Request failed (${response.status})`
    throw new ApiError(response.status, detail, typeof body.code === 'string' ? body.code : null)
  }
  if (response.status === 204) return undefined as T
  return (await response.json()) as T
}

const json = (value: unknown) => JSON.stringify(value)

export const api = {
  me: () => request<MeOut>('/me'),
  people: () => request<UserOut[]>('/admin/users'),
  projectsImport: () => request<ProjectImportReviewOut>('/admin/projects-import'),
  proposeProjectImport: (zohoProjectsId: string, review: 'accept' | 'fix', fixes: Record<string, string>, csrf: string) =>
    request<unknown>(`/admin/projects-import/${encodeURIComponent(zohoProjectsId)}/${review}`, { method: 'POST', body: json(fixes) }, csrf),
  removedCases: () => request<RemovedCaseOut[]>('/admin/removed-cases'),
  restoreCase: (caseId: string, csrf: string) =>
    request<{ id: string; number: string; restored: boolean }>(
      `/cases/${encodeURIComponent(caseId)}/restore`,
      { method: 'POST', body: json({}) },
      csrf,
    ),
  invite: (body: InviteIn, csrf: string) => request<UserOut>('/admin/users', { method: 'POST', body: json(body) }, csrf),
  patchUser: (id: string, body: UserPatch, csrf: string) =>
    request<UserOut>(`/admin/users/${encodeURIComponent(id)}`, { method: 'PATCH', body: json(body) }, csrf),
  settings: () => request<SettingOut[]>('/admin/settings'),
  featureFlags: () => request<FeatureFlagOut[]>('/admin/feature-flags'),
  putFeatureFlag: (id: string, value: unknown, confirmed: boolean, csrf: string) =>
    request<FeatureFlagOut>(`/admin/feature-flags/${encodeURIComponent(id)}`, { method: 'PUT', body: json({ value, confirmed }) }, csrf),
  automation: () => request<AutomationScoreboardOut>('/automation'),
  setAutomationClass: (actionClass: ActionClass, mode: AutonomyMode, csrf: string) =>
    request<AutomationScoreboardOut>(
      `/automation/classes/${encodeURIComponent(actionClass)}`,
      { method: 'PUT', body: json({ mode }) },
      csrf,
    ),
  recomputeAutomation: (csrf: string) => request<AutomationScoreboardOut>('/automation/recompute', { method: 'POST' }, csrf),
  setAutomationThresholds: (minItems: number, minAgreementPct: number, csrf: string) =>
    request<AutomationScoreboardOut>(
      '/automation/thresholds',
      { method: 'PUT', body: json({ min_items: minItems, min_agreement_pct: minAgreementPct }) },
      csrf,
    ),
  putSetting: (key: string, value: unknown, csrf: string) =>
    request<SettingOut>(`/admin/settings/${encodeURIComponent(key)}`, { method: 'PUT', body: json({ value }) }, csrf),
  audit: (query?: AuditListQuery) => {
    const params = new URLSearchParams()
    if (query?.cursor) params.set('cursor', query.cursor)
    if (query?.actor) params.set('actor', query.actor)
    if (query?.actionGroup) params.set('action_group', query.actionGroup)
    if (query?.caseRef) params.set('case', query.caseRef)
    const suffix = params.size ? `?${params.toString()}` : ''
    return request<Page<AuditRow>>(`/admin/audit${suffix}`)
  },
  integrations: () => request<IntegrationHealthOut[]>('/admin/integrations'),
  connectorBudgets: () => request<ConnectorBudgetOut[]>('/admin/connector-budgets'),
  integrationConfig: () => request<IntegrationConfigOut[]>('/admin/integrations/configuration'),
  zohoQuota: () => request<ZohoQuotaOut>('/admin/zoho-quota'),
  rileyTriage: (deskTicketId: string) => request<RileyTriageOut>(`/admin/riley/triage/${encodeURIComponent(deskTicketId)}`),
  rileyRegenerate: (deskTicketId: string, csrf: string) =>
    request<RileyOpOut>('/admin/riley/ops/regenerate', { method: 'POST', body: json({ desk_ticket_id: deskTicketId }) }, csrf),
  rileyPostAnalysis: (deskTicketId: string, csrf: string) =>
    request<RileyOpOut>('/admin/riley/ops/post_analysis', { method: 'POST', body: json({ desk_ticket_id: deskTicketId }) }, csrf),
  rileyLabel: (body: RileyLabelIn, csrf: string) =>
    request<RileyOpOut>('/admin/riley/ops/label', { method: 'POST', body: json(body) }, csrf),
  rileyOrg: (org: string) => request<RileyOrgOut>(`/admin/riley/org/${encodeURIComponent(org)}`),
  rileySaveHandling: (org: string, handling: string, baseSha256: string, csrf: string) =>
    request<RileyOrgOut>(
      `/admin/riley/org/${encodeURIComponent(org)}/handling`,
      { method: 'PUT', body: json({ handling, base_sha256: baseSha256 }) },
      csrf,
    ),
  liveness: () => request<LivenessOut>('/liveness'),
  hubIdentities: () => request<HubIdentityOut[]>('/hub/identities'),
  hubCreateIdentity: (body: HubIdentityCreate, csrf: string) =>
    request<HubIdentityKeyOut>('/hub/identities', { method: 'POST', body: json(body) }, csrf),
  hubPatchIdentity: (id: string, body: HubIdentityPatch, csrf: string) =>
    request<HubIdentityOut>(`/hub/identities/${encodeURIComponent(id)}`, { method: 'PATCH', body: json(body) }, csrf),
  hubRotateIdentity: (id: string, csrf: string) =>
    request<HubIdentityKeyOut>(`/hub/identities/${encodeURIComponent(id)}/rotate`, { method: 'POST' }, csrf),
  hubRevokeIdentity: (id: string, csrf: string) =>
    request<HubIdentityOut>(`/hub/identities/${encodeURIComponent(id)}/revoke`, { method: 'POST' }, csrf),
  hubCalls: (identity?: string) =>
    request<HubCallRow[]>(`/hub/calls?limit=50${identity ? `&identity=${encodeURIComponent(identity)}` : ''}`),
  hubLiveness: () => request<HubLivenessOut>('/hub/liveness'),
  caseByNumber: (reference: string) => request<{ id: string; number: string }>(`/cases/by-number/${encodeURIComponent(reference)}`),
  caseContext: (caseId: string) => request<CaseContextOut>(`/cases/${encodeURIComponent(caseId)}/context`),
  techRates: () => request<CostRateOut[]>('/rates/tech'),
  rileyRate: () => request<RileyRateOut>('/rates/riley'),
  setTechRate: (body: CostRateIn, csrf: string) => request<CostRateOut>('/rates/tech', { method: 'POST', body: json(body) }, csrf),
  contractorRates: (contractorId: string) => request<ContractorRateOut[]>(`/rates/contractors/${encodeURIComponent(contractorId)}`),
  search: (query: string) => request<SearchHit[]>(`/search?q=${encodeURIComponent(query)}`),
  notifications: () => request<NotificationPrefsOut>('/me/notifications'),
  report: (id: string) => request<ReportRunOut>(`/reports/${encodeURIComponent(id)}`),
  patchNotifications: (body: NotificationPrefsIn, csrf: string) =>
    request<NotificationPrefsOut>('/me/notifications', { method: 'PATCH', body: json(body) }, csrf),
  putSignature: (signatureHtml: string, csrf: string) =>
    request<MeOut>('/me/signature', { method: 'PUT', body: json({ signature_html: signatureHtml }) }, csrf),
  putUiMode: (uiMode: 'full' | 'simple', csrf: string) =>
    request<MeOut>('/me/ui-mode', { method: 'PUT', body: json({ ui_mode: uiMode }) }, csrf),
  connectDesk: (csrf: string) => request<{ authorize_url: string }>('/auth/desk/connect?return_to=%2Fme', { method: 'POST' }, csrf),
  connectCalendar: (csrf: string) => request<{ authorize_url: string }>('/auth/calendar/connect?return_to=%2Fme', { method: 'POST' }, csrf),
  disconnectCalendar: (csrf: string) => request<void>('/auth/calendar/disconnect', { method: 'POST' }, csrf),
  connectMail: (csrf: string) => request<{ authorize_url: string }>('/auth/zoho/start?return_to=%2Fme', { method: 'POST' }, csrf),
  disconnectMail: (csrf: string) => request<void>('/auth/mail/disconnect', { method: 'POST' }, csrf),
  disconnectUserMail: (id: string, csrf: string) =>
    request<void>(`/admin/users/${encodeURIComponent(id)}/mail/disconnect`, { method: 'POST' }, csrf),
  knowledge: (query?: string) => {
    const suffix = query ? `?q=${encodeURIComponent(query)}` : ''
    return request<KnowledgeEntryOut[]>(`/knowledge${suffix}`)
  },
  knowledgeProposals: () => request<KnowledgeProposalOut[]>('/knowledge/proposals'),
  proposeKnowledge: (body: KnowledgeProposalIn, csrf: string) =>
    request<KnowledgeProposalOut>('/knowledge/proposals', { method: 'POST', body: json(body) }, csrf),
  decideKnowledge: (id: string, body: KnowledgeDecisionIn, csrf: string) =>
    request<KnowledgeProposalOut>(`/knowledge/proposals/${encodeURIComponent(id)}/decision`, { method: 'POST', body: json(body) }, csrf),
  snippets: (audience?: SnippetAudience) =>
    request<SnippetOut[]>(`/snippets${audience ? `?audience=${encodeURIComponent(audience)}` : ''}`),
  adminSnippets: (includeInactive = true) =>
    request<SnippetOut[]>(`/admin/snippets?include_inactive=${includeInactive ? 'true' : 'false'}`),
  createSnippet: (body: SnippetUpsertIn, csrf: string) =>
    request<SnippetOut>('/admin/snippets', { method: 'POST', body: json(body) }, csrf),
  updateSnippet: (key: string, body: SnippetPatchIn, csrf: string) =>
    request<SnippetOut>(`/admin/snippets/${encodeURIComponent(key)}`, { method: 'PUT', body: json(body) }, csrf),
  snippetHistory: (key: string) => request<SnippetVersionOut[]>(`/admin/snippets/${encodeURIComponent(key)}/history`),
  revertSnippet: (key: string, version: number, csrf: string) =>
    request<SnippetOut>(`/admin/snippets/${encodeURIComponent(key)}/revert`, { method: 'POST', body: json({ version }) }, csrf),
  dryRunDeskSnippets: (csrf: string) => request<DeskSnippetDryRunOut>('/admin/snippets/import/desk/dry-run', { method: 'POST' }, csrf),
  applyDeskSnippets: (candidates: DeskSnippetCandidate[], csrf: string) =>
    request<DeskSnippetApplyOut>('/admin/snippets/import/desk/apply', { method: 'POST', body: json({ candidates }) }, csrf),
}

export type AdminApi = typeof api

let testReads: Partial<AdminApi> | null = null

/**
 * src/test/setup.ts installs fixture-backed reads for every vitest run, so production code never imports a fixtures
 * module (the build fails if one reaches the bundle). Production always reads `api`.
 */
export function installAdminTestReads(reads: Partial<AdminApi> | null): void {
  if (import.meta.env.MODE !== 'test') throw new Error('Admin test reads are test-only')
  testReads = reads
}

/** The read seam the admin, me and knowledge queries use: the real API, or the test default under Vitest. */
export function reads(): AdminApi {
  return testReads ? { ...api, ...testReads } : api
}
